/**
 * PlanningRunWizard — 5-step Radix Dialog for creating an AI planning run.
 *
 * Steps:
 *   1. Objectives  — what are we trying to achieve
 *   2. Period      — start/end dates + optional theme
 *   3. Constraints — max deals, complexity, channels
 *   4. Data        — which sources are available (drives data-quality score)
 *   5. Menu        — quick menu assumptions
 *   6. Generate    — kick off AI (spinner state 15-25s)
 */
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import { Checkbox } from '../ui/checkbox';
import { useAuth } from '../../context/AuthContext';

const OBJECTIVES = [
  'Fill quiet hours',
  'Grow average spend',
  'Bring in a new local group',
  'Reduce end-of-day waste',
  'Encourage repeat visits',
  'Boost weekend/school-holiday footfall',
  'Test a new item/menu category',
];

const CHANNELS = ['in_store', 'collection', 'delivery', 'social', 'email', 'signage'];

const DATA_SOURCES = [
  { key: 'sales_by_day', label: 'Daily sales (last 90 days)' },
  { key: 'sales_by_hour', label: 'Hourly sales / time of day' },
  { key: 'day_of_week', label: 'Day-of-week sales matrix' },
  { key: 'product_mix', label: 'Product mix / top sellers' },
  { key: 'ingredient_costs', label: 'Ingredient / recipe costs' },
  { key: 'menu_prices', label: 'Menu prices' },
  { key: 'customer_notes', label: 'Owner notes / anecdotes' },
  { key: 'waste_notes', label: 'Waste / near-expiry notes' },
];

export default function PlanningRunWizard({ open, onOpenChange, locationId, onGenerated }) {
  const { api } = useAuth();
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState('');
  const [objectives, setObjectives] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [campaignTheme, setCampaignTheme] = useState('');
  const [maxDeals, setMaxDeals] = useState(3);
  const [discountComfort, setDiscountComfort] = useState('medium');
  const [opsComplexity, setOpsComplexity] = useState('medium');
  const [channels, setChannels] = useState(['in_store']);
  const [dataSources, setDataSources] = useState({}); // key -> availability
  const [menuAssumptions, setMenuAssumptions] = useState('');
  const [busy, setBusy] = useState(false);

  const totalSteps = 6;
  const progress = (step / totalSteps) * 100;

  const reset = () => {
    setStep(1); setTitle(''); setObjectives([]); setStartDate(''); setEndDate('');
    setCampaignTheme(''); setMaxDeals(3); setDiscountComfort('medium');
    setOpsComplexity('medium'); setChannels(['in_store']); setDataSources({});
    setMenuAssumptions(''); setBusy(false);
  };

  const toggleObjective = (o) => {
    setObjectives((s) => (s.includes(o) ? s.filter((x) => x !== o) : [...s, o]));
  };
  const toggleChannel = (c) => {
    setChannels((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));
  };

  const dq = useMemo(() => {
    const items = Object.values(dataSources || {});
    if (items.length === 0) return { rating: 'None', score: 0 };
    const total = items.length;
    const available = items.filter((v) => v === 'available').length;
    const partial = items.filter((v) => v === 'partial').length;
    const score = (available + 0.5 * partial) / total;
    return {
      rating: score >= 0.7 ? 'High' : score >= 0.4 ? 'Medium' : 'Low',
      score: Math.round(score * 100),
    };
  }, [dataSources]);

  const canNext = () => {
    if (step === 1) return title.trim().length > 0 && objectives.length > 0;
    if (step === 2) return startDate && endDate;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        title,
        location_id: locationId,
        objectives,
        start_date: startDate,
        end_date: endDate,
        campaign_theme: campaignTheme || null,
        constraints: {
          max_deals: Number(maxDeals) || 3,
          discount_comfort: discountComfort,
          ops_complexity: opsComplexity,
          channels,
        },
        data_sources: Object.entries(dataSources).map(([k, v]) => ({ source: k, availability: v })),
        menu_assumptions: { notes: menuAssumptions },
      };
      const runRes = await api.post('/deals/planning-runs', payload);
      const runId = runRes.data.id;
      const genRes = await api.post(`/deals/planning-runs/${runId}/generate`);
      toast.success(`Generated ${genRes.data.count} deal suggestion(s)`);
      onGenerated?.(runId);
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate planning run');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { onOpenChange(v); if (!v) reset(); } }}>
      <DialogContent className="max-w-2xl" data-testid="planning-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> New Planning Run
          </DialogTitle>
          <DialogDescription>
            AI-assisted deal planner (Claude Sonnet 5). Roughly {maxDeals} suggestion{maxDeals === 1 ? '' : 's'} in ~15-25 seconds.
          </DialogDescription>
        </DialogHeader>

        <Progress value={progress} className="mb-2" />
        <div className="text-xs text-muted-foreground">Step {step} of {totalSteps}</div>

        <div className="py-3 min-h-[260px]">
          {step === 1 && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="pr-title">Planning run title</Label>
                <Input
                  id="pr-title"
                  placeholder="e.g. July quiet-month rescue"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  data-testid="wizard-title"
                />
              </div>
              <div>
                <Label>What are you trying to achieve?</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {OBJECTIVES.map((o) => (
                    <button
                      key={o}
                      onClick={() => toggleObjective(o)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition ${
                        objectives.includes(o)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background hover:bg-accent'
                      }`}
                      data-testid={`wizard-obj-${o.replace(/[^a-z0-9]/gi, '_').toLowerCase()}`}
                    >{o}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="pr-start">Start date</Label>
                  <Input id="pr-start" type="date" value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    data-testid="wizard-start-date" />
                </div>
                <div>
                  <Label htmlFor="pr-end">End date</Label>
                  <Input id="pr-end" type="date" value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    data-testid="wizard-end-date" />
                </div>
              </div>
              <div>
                <Label htmlFor="pr-theme">Campaign theme (optional)</Label>
                <Input id="pr-theme" placeholder="e.g. 'Summer chill' or 'Back to school'"
                  value={campaignTheme} onChange={(e) => setCampaignTheme(e.target.value)}
                  data-testid="wizard-theme" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="pr-max">Max number of suggested deals</Label>
                <Input id="pr-max" type="number" min="1" max="8" value={maxDeals}
                  onChange={(e) => setMaxDeals(e.target.value)}
                  data-testid="wizard-max-deals" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Comfort with discounts</Label>
                  <div className="flex gap-1 mt-1">
                    {['low', 'medium', 'high'].map((v) => (
                      <button
                        key={v}
                        onClick={() => setDiscountComfort(v)}
                        className={`text-xs px-3 py-1.5 rounded border capitalize ${
                          discountComfort === v ? 'bg-primary text-primary-foreground' : 'bg-background'
                        }`}
                        data-testid={`wizard-discount-${v}`}
                      >{v}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Operational complexity tolerance</Label>
                  <div className="flex gap-1 mt-1">
                    {['low', 'medium', 'high'].map((v) => (
                      <button
                        key={v}
                        onClick={() => setOpsComplexity(v)}
                        className={`text-xs px-3 py-1.5 rounded border capitalize ${
                          opsComplexity === v ? 'bg-primary text-primary-foreground' : 'bg-background'
                        }`}
                      >{v}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <Label>Channels available</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {CHANNELS.map((c) => (
                    <button
                      key={c}
                      onClick={() => toggleChannel(c)}
                      className={`text-xs px-3 py-1.5 rounded-full border capitalize ${
                        channels.includes(c) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-accent'
                      }`}
                    >{c.replace(/_/g, ' ')}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Tell the AI what data it can lean on. This drives the confidence rating.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {DATA_SOURCES.map((s) => (
                  <div key={s.key} className="flex items-center gap-2 p-2 border rounded">
                    <div className="flex-1 text-sm">{s.label}</div>
                    <select
                      className="text-xs border rounded px-1 py-0.5 bg-background"
                      value={dataSources[s.key] || 'missing'}
                      onChange={(e) => setDataSources((d) => ({ ...d, [s.key]: e.target.value }))}
                      data-testid={`wizard-datasource-${s.key}`}
                    >
                      <option value="missing">Missing</option>
                      <option value="partial">Partial</option>
                      <option value="available">Available</option>
                    </select>
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted-foreground">
                Data quality preview: <b>{dq.rating}</b> ({dq.score}%)
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-3">
              <Label htmlFor="pr-menu">Menu assumptions (optional)</Label>
              <Textarea
                id="pr-menu"
                rows={6}
                placeholder={`e.g.\n- Iced Latte £4.20, ~65% gross margin\n- Signature Sandwich £8.50\n- Brownie £3.20`}
                value={menuAssumptions}
                onChange={(e) => setMenuAssumptions(e.target.value)}
                data-testid="wizard-menu-notes"
              />
              <p className="text-xs text-muted-foreground">
                Rough prices and margins improve the AI's pricing suggestions.
              </p>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-3 text-center py-6">
              {busy ? (
                <>
                  <Loader2 className="w-10 h-10 animate-spin mx-auto text-primary" />
                  <div className="font-medium">Generating with Claude Sonnet 5…</div>
                  <p className="text-xs text-muted-foreground">This usually takes 15-25 seconds.</p>
                </>
              ) : (
                <>
                  <Sparkles className="w-10 h-10 mx-auto text-primary" />
                  <div className="font-medium">Ready to generate {maxDeals} deal suggestion{maxDeals === 1 ? '' : 's'}</div>
                  <p className="text-xs text-muted-foreground">
                    You'll be able to approve, edit, or ask the AI to try again on each one.
                  </p>
                  <div className="text-xs text-muted-foreground">
                    Data confidence: <b>{dq.rating}</b>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <Button
            variant="ghost"
            disabled={step === 1 || busy}
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            data-testid="wizard-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {step < totalSteps ? (
            <Button
              onClick={() => setStep((s) => s + 1)}
              disabled={!canNext()}
              data-testid="wizard-next-btn"
            >
              Next <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={busy} data-testid="wizard-generate-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Generate
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
